import { buttonStyle } from 'components/buttons/buttonStyle';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { useForm } from 'react-hook-form';
import { useSetMyTargetsMutation } from 'store/api/employees';
import { EmployeeTargetsDto } from 'types/employees';
import { t } from 'utils/getTranslations';
import TargetsFields, { TargetsForm } from './TargetsFields';

type Props = {
    onClose: () => void;
    annualTargetHours?: number | null;
    plannedWorkdaysOff?: number | null;
};

/** Edits the signed-in employee's own targets; the dashboard tab and the employee page both open it. */
const EditTargetsModal = ({ onClose, annualTargetHours, plannedWorkdaysOff }: Props) => {
    const [setMyTargets, { isLoading }] = useSetMyTargetsMutation();
    const { openModal, closeModal } = useModal();
    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<TargetsForm>({
        mode: 'onChange',
        defaultValues: { annualTargetHours: annualTargetHours ?? null, plannedWorkdaysOff: plannedWorkdaysOff ?? null },
    });

    const save = async (targets: EmployeeTargetsDto) => {
        try {
            await setMyTargets(targets).unwrap();
            onClose();
        } catch {
            // The API layer has already shown the error toast; the modal stays open for another try.
        }
    };

    const onSubmit = (values: TargetsForm) =>
        save({
            annualTargetHours: values.annualTargetHours ?? null,
            plannedWorkdaysOff: values.plannedWorkdaysOff ?? null,
        });

    // Removing keeps the stored days off, not whatever has been typed since the modal opened.
    const removeButton =
        typeof annualTargetHours === 'number' ? (
            <Button
                {...buttonStyle({ variant: 'outlined', color: 'error' }, 'text-sm font-normal')}
                disabled={isLoading}
                onClick={() =>
                    openModal(
                        <ConfirmationModal
                            titleId="dashboard.progress-tracking.remove-target-title"
                            onCancel={closeModal}
                            onConfirm={() => {
                                closeModal();
                                void save({ annualTargetHours: null, plannedWorkdaysOff: plannedWorkdaysOff ?? null });
                            }}
                            content={
                                <p className="text-body1">
                                    {t('dashboard.progress-tracking.remove-target-confirmation')}
                                </p>
                            }
                        />
                    )
                }
            >
                {t('dashboard.progress-tracking.remove-target')}
            </Button>
        ) : undefined;

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
        >
            <ModalHeader title={t('dashboard.progress-tracking.edit-targets')} onCancel={onClose} />
            <div className="overflow-y-auto p-6 max-sm:px-4">
                <TargetsFields control={control} />
            </div>
            <ModalFooter
                extraButton={removeButton}
                onCancel={onClose}
                onSave={() => {
                    void handleSubmit(onSubmit)();
                }}
                disableSave={!isValid || isLoading}
            />
        </form>
    );
};

export default EditTargetsModal;
